import { ClinicalTrial, CreateInquiryVM, Ecrf, Inquiry, InquiryHistoryPage, Patient, PatientAdmission, SaveFormDataVM, FormData, AuditTrailPage } from "./models";

export const getPatientAdmission: () => Promise<Array<PatientAdmission>> = async () => {
    const response = await fetch("/api/PatientAdmissions", {
        method: "GET",
        headers: {
            Accept: "application/json",
        },
    });

    if (response.status === 200) {
        const info: Array<PatientAdmission> = await response.json();
        return info;
    }

    return [] as Array<PatientAdmission>;
}

export const getPatients: () => Promise<Array<Patient>> = async () => {
    const response = await fetch("/api/Patient", {
        method: "GET",
        headers: {
            Accept: "application/json",
        },
    });

    if (response.status === 200) {
        const info: Array<Patient> = await response.json();
        return info;
    }

    return [] as Array<Patient>;
}

export const postPatient: (patient: Patient) => Promise<boolean> = async (patient: Patient) => {
    const response = await fetch("/api/Patient/Add", {
        method: "POST",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        },
        body: JSON.stringify(patient)
    });

    if (response.status === 200) {
        const info: number = await response.json();
        return true;
    }

    return false;
}

export const deletePatient: (patientId: number) => Promise<boolean> = async (patientId: number) => {
    const response = await fetch(`/api/Patient/Delete/${patientId}`, {
        method: "POST",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        },
    });

    if (response.status === 200) {
        return true;
    }

    return false;
}

export const postPatientAdmission: (patientAdmission: PatientAdmission) => Promise<boolean> = async (patientAdmission: PatientAdmission) => {
    const response = await fetch("/api/PatientAdmissions/Add", {
        method: "POST",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        },
        body: JSON.stringify(patientAdmission)
    });

    if (response.status === 200) {
        const info: number = await response.json();
        return true;
    }

    return false;
}

export const deletePatientAdmission: (patientAdmissionId: number) => Promise<boolean> = async (patientAdmissionId: number) => {
    const response = await fetch(`/api/PatientAdmissions/Delete/${patientAdmissionId}`, {
        method: "POST",
        headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
        },
    });

    if (response.status === 200) {
        return true;
    }

    return false;
}

export const addClinicalTrial: (trial: ClinicalTrial) => Promise<boolean> = async (trial: ClinicalTrial) => {
    const response = await fetch("/api/ClinicalTrial/Add", {
        method: "POST",
        headers: {
            'Accept': 'application/json',
            'Content-type': 'application/json',
        },
        body: JSON.stringify({ title: trial.title, description: trial.description })
        ,
    });

    if (response.status === 200) {
        return true;
    }

    return false;
}

export const getECRFS: () => Promise<Array<Ecrf>> = async () => {
    const response = await fetch(`/api/Forms`, {
        method: "GET",
        headers: {
            Accept: "application/json",
        },
    });

    if (response.status === 200) {
        const info: Array<Ecrf> = await response.json();
        return info;
    }

    return [] as Array<Ecrf>;
}

export const saveFormValues: (formValues: SaveFormDataVM) => Promise<boolean> = async (formValues: SaveFormDataVM) => {
    const response = await fetch("/api/Forms", {
        method: "POST",
        headers: {
            'Accept': 'application/json',
            'Content-type': 'application/json',
        },
        body: JSON.stringify(formValues),
    });

    if (response.status === 200) {
        return true;
    }

    return false;
}

export const getFormValues: (formId: number, sectionId: number, admissionId: number) => Promise<FormData | null> = async (formId: number, sectionId: number, admissionId: number) => {
    const response = await fetch(`/api/Forms/Data?formId=${formId}&sectionId=${sectionId}&admissionId=${admissionId}`, {
        method: "GET",
        headers: {
            'Accept': 'application/json',
            'Content-type': 'application/json',
        },
        body: null,
    });

    if (response.status === 200) {
        try {
            return await response.json();
        } catch {
            return null;
        }
    }

    return null;
}

export const createInquiry = async (vm: CreateInquiryVM): Promise<Inquiry | null> => {
    const response = await fetch("/api/Inquiry", {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify(vm),
    });
    if (response.status === 200) return await response.json();
    return null;
};

export const getMyInquiries = async (): Promise<Inquiry[]> => {
    const response = await fetch("/api/Inquiry/Mine", {
        method: "GET",
        headers: { Accept: "application/json" },
    });
    if (response.status === 200) return await response.json();
    return [];
};

export const getInquiriesForSection = async (formId: number, sectionId: number, patientAdmissionId: number): Promise<Inquiry[]> => {
    const response = await fetch(
        `/api/Inquiry/ForSection?formId=${formId}&sectionId=${sectionId}&patientAdmissionId=${patientAdmissionId}`,
        { method: "GET", headers: { Accept: "application/json" } }
    );
    if (response.status === 200) return await response.json();
    return [];
};

export const resolveInquiry = async (id: number): Promise<boolean> => {
    const response = await fetch(`/api/Inquiry/Resolve/${id}`, {
        method: "POST",
        headers: { Accept: "application/json" },
    });
    return response.status === 200;
};

export const getInquiryHistory = async (skip: number, take: number): Promise<InquiryHistoryPage | null> => {
    const response = await fetch(`/api/Inquiry/History?skip=${skip}&take=${take}`, {
        method: "GET",
        headers: { Accept: "application/json" },
    });
    if (response.status === 200) return await response.json();
    return null;
};


export const getAuditTrail: (skip: number, take: number, userName?: string, action?: string) => Promise<AuditTrailPage | null> = async (skip: number, take: number, userName?: string, action?: string) => {
    const params = new URLSearchParams({
        skip: skip.toString(),
        take: take.toString(),
        ...(userName ? { userName } : {}),
        ...(action ? { action } : {}),
    });
    const response = await fetch(`/api/AuditTrail?${params}`, {
        method: "GET",
        headers: {
            'Accept': 'application/json',
            'Content-type': 'application/json',
        },
        body: null,
    });

    if (response.status === 200) {
        try {
            return await response.json();
        } catch {
            return null;
        }
    }

    return null;
}